import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Search, X, MessageSquare, PhoneCall } from "lucide-react";

export default function Conversations() {
  const [chats, setChats] = useState([]);
  const [q, setQ] = useState("");
  const [handoverOnly, setHandoverOnly] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q };
      if (handoverOnly === "handover") params.handover = "true";
      const { data } = await api.get("/admin/assistant/chats", { params });
      setChats(data);
    } catch { toast.error("Failed to load conversations"); }
    setLoading(false);
  }, [q, handoverOnly]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (sid) => {
    setDetailLoading(true);
    setSelected({ session_id: sid, messages: [] });
    try {
      const { data } = await api.get(`/admin/assistant/chats/${sid}`);
      setSelected(data);
    } catch { toast.error("Failed to load conversation"); setSelected(null); }
    setDetailLoading(false);
  };

  const handoverCount = chats.filter((c) => c.handover).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-4xl text-deep-navy">Conversations</h1>
          <p className="text-charcoal/60">{chats.length} chats · {handoverCount} handovers</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input data-testid="chats-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search messages, name, number"
                 className="w-full border border-[#E5E5E5] bg-white pl-10 pr-4 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue" />
        </div>
        <select data-testid="chats-filter" value={handoverOnly} onChange={(e) => setHandoverOnly(e.target.value)} className="border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm">
          <option value="all">All conversations</option>
          <option value="handover">Handovers only</option>
        </select>
      </div>

      <div className="bg-white border border-[#E5E5E5] overflow-hidden" data-testid="chats-table">
        <table className="w-full text-sm">
          <thead className="bg-off-white border-b border-[#E5E5E5]">
            <tr className="text-left text-charcoal/50">
              <th className="px-4 py-3 font-medium">First question</th>
              <th className="px-4 py-3 font-medium">Turns</th>
              <th className="px-4 py-3 font-medium">Handover</th>
              <th className="px-4 py-3 font-medium hidden md:table-cell">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-charcoal/40">Loading…</td></tr>
            ) : chats.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-charcoal/40" data-testid="chats-empty">No conversations yet.</td></tr>
            ) : chats.map((c) => (
              <tr key={c.session_id} className="border-b border-[#E5E5E5] hover:bg-off-white cursor-pointer" onClick={() => openDetail(c.session_id)} data-testid={`chat-row-${c.session_id}`}>
                <td className="px-4 py-3 max-w-md">
                  <p className="font-medium text-charcoal truncate">{c.preview || "—"}</p>
                  <p className="text-charcoal/50 text-xs truncate">{c.last_message}</p>
                </td>
                <td className="px-4 py-3 text-charcoal/70">{c.turns}</td>
                <td className="px-4 py-3">
                  {c.handover ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-green-100 text-green-800">
                      <PhoneCall size={12} /> {c.handover_name || "Yes"}{c.handover_phone ? ` · ${c.handover_phone}` : ""}
                    </span>
                  ) : (
                    <span className="text-xs text-charcoal/40">—</span>
                  )}
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-charcoal/50 text-xs">{c.updated_at ? new Date(c.updated_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="chat-detail">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center p-6 border-b border-[#E5E5E5] sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <MessageSquare size={18} className="text-restrained-blue" />
                <h2 className="font-serif text-2xl text-deep-navy">Conversation</h2>
              </div>
              <button onClick={() => setSelected(null)} data-testid="chat-detail-close" className="text-charcoal/50 hover:text-charcoal"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {selected.handover && (
                <div className="bg-green-50 border border-green-200 p-3 text-sm" data-testid="chat-handover-banner">
                  <p className="text-green-800 font-medium flex items-center gap-2"><PhoneCall size={14} /> Handover to Raman</p>
                  <p className="text-green-900/80 mt-1">{selected.handover_name} · {selected.handover_phone}</p>
                  {selected.handover_at && <p className="text-green-900/50 text-xs mt-1">{new Date(selected.handover_at).toLocaleString()}</p>}
                </div>
              )}
              {detailLoading ? (
                <p className="text-charcoal/40 text-sm text-center py-8">Loading transcript…</p>
              ) : (
                <div className="space-y-3" data-testid="chat-transcript">
                  {(selected.messages || []).map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl whitespace-pre-wrap ${m.role === "user" ? "bg-deep-navy text-white rounded-br-sm" : "bg-off-white border border-[#E9E9E9] text-charcoal rounded-bl-sm"}`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {(!selected.messages || selected.messages.length === 0) && <p className="text-sm text-charcoal/40">No messages.</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
